import { useEffect, useState } from "react";
import { Layout } from "../components/Layout";
import { FreddyMark } from "../components/FreddyLogo";
import { ScoreBadge, VerdictPill } from "../components/VerdictBadge";
import { api } from "../lib/api";
import { X, Minimize2, Chrome } from "lucide-react";
import { cn } from "../lib/utils";

// Emulates how the extension overlay would render on a Manheim VDP
export default function ExtensionPreview() {
  const [sample, setSample] = useState(null);
  const [minimized, setMinimized] = useState(false);

  useEffect(() => {
    api.get("/vehicles?verdict=strong_buy&limit=1").then(async (r) => {
      if (r.data.length > 0) {
        const s = await api.get(`/extension/score/${r.data[0].vin}`);
        setSample(s.data);
      }
    });
  }, []);

  return (
    <Layout variant="corporate">
      <div className="mb-6 flex items-end justify-between">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
            <Chrome size={12} /> Browser extension preview
          </div>
          <h1 className="text-3xl font-bold text-navy mt-1">Freddy Buy Signal overlay</h1>
          <p className="text-sm text-slate-500 mt-1 font-medium max-w-2xl">
            This is how the overlay appears on Manheim, vAuto, AutoTrader, Cars.com, Facebook Marketplace, and OVE/ADESA.
            Buyers get an instant verdict without leaving the VDP.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        {/* Fake Manheim VDP */}
        <div className="xl:col-span-3 rounded-xl overflow-hidden border border-slate-200 relative min-h-[600px] bg-slate-100" data-testid="mock-vdp">
          <div className="bg-[#1a4b8f] text-white px-5 py-3 flex items-center gap-3 text-sm">
            <div className="font-bold tracking-wide">MANHEIM</div>
            <div className="h-4 w-px bg-white/30" />
            <div className="text-white/80 text-xs">Simulcast · DC Lane 14 · Run 142</div>
          </div>
          <div className="p-6 bg-white">
            <div className="aspect-[16/9] bg-slate-200 rounded-md mb-4 flex items-center justify-center text-slate-400 text-xs uppercase tracking-wider font-bold">
              Vehicle photo · {sample ? `${sample.year} ${sample.make} ${sample.model}` : "—"}
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-1">
              {sample ? `${sample.year} ${sample.make} ${sample.model} ${sample.trim}` : "Loading sample VIN…"}
            </h2>
            <div className="text-sm text-slate-500 mb-4 tabular">VIN {sample?.vin || "—"}</div>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div><div className="text-xs text-slate-500 uppercase tracking-wider font-semibold">MMR</div><div className="font-bold tabular">${sample?.mmr_current?.toLocaleString() || "—"}</div></div>
              <div><div className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Retail avg</div><div className="font-bold tabular">${sample?.comp_retail_avg?.toLocaleString() || "—"}</div></div>
              <div><div className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Recon</div><div className="font-bold tabular">${sample?.recon_estimate?.toLocaleString() || "—"}</div></div>
            </div>
          </div>

          {/* Floating overlay */}
          {sample && (
            <div className="absolute bottom-5 right-5 z-20" data-testid="extension-overlay">
              {minimized ? (
                <button onClick={() => setMinimized(false)}
                        className="w-14 h-14 rounded-full bg-freddy-red shadow-lg flex items-center justify-center animate-pulse-red"
                        aria-label="expand extension">
                  <FreddyMark size={28} />
                </button>
              ) : (
                <div className={cn("w-[380px] bg-white rounded-xl shadow-2xl overflow-hidden", `verdict-${sample.verdict}`)}>
                  <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200/70 bg-white">
                    <div className="flex items-center gap-2">
                      <FreddyMark size={20} />
                      <span className="font-bold text-navy text-sm">Freddy Buy Signal</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => setMinimized(true)} className="p-1 hover:bg-slate-100 rounded"><Minimize2 size={12} /></button>
                      <button className="p-1 hover:bg-slate-100 rounded"><X size={12} /></button>
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="flex items-start gap-3 mb-3">
                      <ScoreBadge score={sample.score} verdict={sample.verdict} size="lg" />
                      <div className="flex-1">
                        <VerdictPill verdict={sample.verdict} />
                        <div className="text-sm font-semibold text-navy mt-1 leading-tight">{sample.year} {sample.make} {sample.model} {sample.trim}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">Assigned · <span className="font-semibold text-navy">{sample.assigned_rooftop}</span></div>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-2 py-3 border-y border-slate-200">
                      <Metric label="Target" value={`$${sample.target_price.toLocaleString()}`} />
                      <Metric label="Proj gross" value={`+$${sample.projected_gross.toLocaleString()}`} tone="strong" />
                      <Metric label="Retail velocity" value={`${sample.avg_retail_days_to_sell}d`} />
                    </div>

                    <div className="py-3">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Why</div>
                      <ul className="space-y-1">
                        {sample.justification.slice(0, 3).map((j, i) => (
                          <li key={i} className="text-xs text-slate-700 flex gap-2"><span className="text-freddy-red font-bold">·</span>{j}</li>
                        ))}
                      </ul>
                    </div>

                    <div className="grid grid-cols-3 gap-2 py-3 border-t border-slate-200 mb-3">
                      <Metric label="MMR" value={`$${sample.mmr_current.toLocaleString()}`} size="sm" />
                      <Metric label="Retail avg" value={`$${sample.comp_retail_avg.toLocaleString()}`} size="sm" />
                      <Metric label="Recon" value={`$${sample.recon_estimate.toLocaleString()}`} size="sm" />
                    </div>

                    <div className="space-y-2">
                      <button className="w-full bg-freddy-red hover:bg-freddy-redDark text-white text-xs font-bold uppercase tracking-wider py-2.5 rounded-md">
                        Alert UCM · lock max ${sample.target_price.toLocaleString()}
                      </button>
                      <div className="flex gap-2">
                        <button className="flex-1 bg-navy hover:bg-navy-800 text-white text-[11px] font-bold py-2 rounded-md">Log decision</button>
                        <button className="flex-1 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-[11px] font-bold py-2 rounded-md">Pass</button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Support panel */}
        <div className="xl:col-span-2 space-y-4">
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h3 className="font-bold text-navy mb-3">Supported sites</h3>
            <ul className="space-y-2 text-sm text-slate-700">
              {["manheim.com", "simulcast.manheim.com", "vauto.com", "ove.com", "adesa.com", "autotrader.com", "cars.com", "facebook.com/marketplace", "ourisman.com/appraisal"].map((s) => (
                <li key={s} className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-freddy-red rounded-full" />
                  <span className="tabular text-xs font-semibold">{s}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-navy text-white rounded-xl p-5 relative grain overflow-hidden">
            <div className="relative z-10">
              <h3 className="font-bold mb-2">Install the extension</h3>
              <p className="text-xs text-white/70 mb-4">Manifest V3 Chrome extension. One click on the toolbar gives your buyers the verdict on every VIN they view.</p>
              <button className="bg-freddy-red hover:bg-freddy-redDark text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-md inline-flex items-center gap-2">
                <Chrome size={14} /> Add to Chrome
              </button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function Metric({ label, value, tone, size = "md" }) {
  const toneCls = tone === "strong" ? "text-verdict-strongText" : "text-navy";
  const sz = size === "sm" ? "text-xs" : "text-sm";
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
      <div className={cn("font-bold tabular leading-tight", sz, toneCls)}>{value}</div>
    </div>
  );
}
